import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Users, 
  Buildings, 
  Folder, 
  CurrencyDollar, 
  Clock, 
  TrendUp,
  Warning,
  Target,
  Sliders,
  Eye,
  EyeSlash,
  ArrowsClockwise,
  DotsSixVertical,
  Lightning,
  ChartBar,
  MapPin
} from '@phosphor-icons/react';
import { getDashboardStats, getRecentActivity, getDashboardLayout, saveDashboardLayout, getConventionsPendingSignature, getMissionsByTerritory } from '../services/api';
import { QuotesGlobalKpi, QuotesByTerritoryPanel } from '../components/QuotesConversionWidget';
import { PageHero } from '../components/motion/PageHero';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const COLORS = ['#2D5A4C', '#D47A62', '#8DB0A1', '#E8A365', '#62A8D4', '#5E6D66'];

const territories = [
  { name: 'Guadeloupe', abbr: 'GP' },
  { name: 'Martinique', abbr: 'MQ' },
  { name: 'Guyane', abbr: 'GY' },
  { name: 'Réunion', abbr: 'RE' },
  { name: 'Mayotte', abbr: 'YT' },
  { name: 'Saint-Martin', abbr: 'SM' },
  { name: 'Hexagone', abbr: 'HEX' },
];

const WIDGET_LABELS = {
  kpi_prospects: 'Prospects actifs',
  kpi_membres: 'Membres actifs',
  kpi_dossiers: 'Dossiers ouverts',
  kpi_contributions: 'Contributions (mois)',
  kpi_conversion: 'Taux de conversion',
  kpi_paiements: 'Paiements en attente',
  kpi_relances: 'Relances du jour',
  chart_pipeline: 'Pipeline commercial',
  chart_dossiers: 'Statuts des dossiers',
  stat_territoires: 'Repartition territoriale',
  activite_recente: 'Activite recente',
  taches_urgentes: 'Taches urgentes',
  impact_ess: 'Impact ESS',
};

const WIDGET_ICONS = {
  kpi_prospects: Users,
  kpi_membres: Buildings,
  kpi_dossiers: Folder,
  kpi_contributions: CurrencyDollar,
  kpi_conversion: TrendUp,
  kpi_paiements: CurrencyDollar,
  kpi_relances: Clock,
  chart_pipeline: Target,
  chart_dossiers: ChartBar,
  stat_territoires: MapPin,
  activite_recente: Lightning,
  taches_urgentes: Warning,
  impact_ess: ChartBar,
};

const WIDGET_CATEGORIES = {
  kpi_prospects: 'kpi', kpi_membres: 'kpi', kpi_dossiers: 'kpi', kpi_contributions: 'kpi',
  kpi_conversion: 'kpi', kpi_paiements: 'kpi', kpi_relances: 'kpi',
  chart_pipeline: 'chart', chart_dossiers: 'chart', stat_territoires: 'chart',
  activite_recente: 'feed', taches_urgentes: 'feed', impact_ess: 'chart',
};

const ALL_WIDGET_IDS = [
  'kpi_prospects', 'kpi_membres', 'kpi_dossiers', 'kpi_contributions',
  'kpi_conversion', 'kpi_paiements', 'kpi_relances',
  'chart_pipeline', 'chart_dossiers', 'stat_territoires', 'activite_recente',
  'taches_urgentes', 'impact_ess',
];

const DEFAULT_LAYOUT = ALL_WIDGET_IDS.slice(0, 11).map(id => ({ id, visible: true }));

function SortableWidgetItem({ item, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const Icon = WIDGET_ICONS[item.id] || Eye;
  const cat = WIDGET_CATEGORIES[item.id] || 'kpi';
  const catColors = { kpi: 'bg-blue-100 text-blue-700', chart: 'bg-purple-100 text-purple-700', feed: 'bg-amber-100 text-amber-700' };

  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors ${item.visible ? 'bg-white' : 'bg-[#F9F8F5] opacity-70'}`} data-testid={`layout-item-${item.id}`}>
      <button className="cursor-grab active:cursor-grabbing text-[#8DB0A1] hover:text-[#2D5A4C]" {...attributes} {...listeners}>
        <DotsSixVertical size={16} weight="bold" />
      </button>
      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${item.visible ? catColors[cat] : 'bg-gray-100 text-gray-400'}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1A2421] truncate">{WIDGET_LABELS[item.id] || item.id}</p>
        <p className="text-[10px] text-[#8DB0A1] uppercase">{cat === 'kpi' ? 'Indicateur' : cat === 'chart' ? 'Graphique' : 'Section'}</p>
      </div>
      <Switch checked={item.visible} onCheckedChange={(v) => onToggle(item.id, v)} />
    </div>
  );
}

export function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [tempLayout, setTempLayout] = useState(DEFAULT_LAYOUT);
  const [conventionsPending, setConventionsPending] = useState({ items: [], total: 0 });
  const [missionsByTerritory, setMissionsByTerritory] = useState({ items: [], total_missions: 0, total_amount: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes, layoutRes, convRes, missRes] = await Promise.all([
          getDashboardStats(),
          getRecentActivity(8),
          getDashboardLayout(),
          getConventionsPendingSignature().catch(() => ({ data: { items: [], total: 0 } })),
          getMissionsByTerritory().catch(() => ({ data: { items: [], total_missions: 0, total_amount: 0 } })),
        ]);
        setStats(statsRes.data);
        setActivities(activityRes.data);
        setConventionsPending(convRes.data);
        setMissionsByTerritory(missRes.data);
        if (layoutRes.data?.layout) {
          // Merge in any missing widgets
          const savedIds = layoutRes.data.layout.map(l => l.id);
          const merged = [...layoutRes.data.layout];
          for (const wid of ALL_WIDGET_IDS) {
            if (!savedIds.includes(wid)) merged.push({ id: wid, visible: false });
          }
          setLayout(merged);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveLayout = async () => {
    try {
      await saveDashboardLayout(tempLayout);
      setLayout(tempLayout);
      setCustomizeOpen(false);
      toast.success('Disposition sauvegardee');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleResetLayout = () => {
    setTempLayout(DEFAULT_LAYOUT);
  };

  const handleToggleWidget = (id, visible) => {
    setTempLayout(prev => prev.map(w => w.id === id ? { ...w, visible } : w));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTempLayout(prev => {
      const oldIndex = prev.findIndex(w => w.id === active.id);
      const newIndex = prev.findIndex(w => w.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Build visibility map from layout order
  const w = {};
  for (const item of layout) w[item.id] = item.visible;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  const pipelineData = stats?.pipeline_stages ? 
    Object.entries(stats.pipeline_stages).map(([key, value]) => ({
      name: key.replace('_', ' ').charAt(0).toUpperCase() + key.replace('_', ' ').slice(1),
      value
    })) : [];

  const dossierData = stats?.dossier_statuses ?
    Object.entries(stats.dossier_statuses)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: key.replace('_', ' '),
        value
      })) : [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Tableau de bord"
        subtitle="Vue d'ensemble de l'activité coopérative ESS Outre-mer"
        icon={<ChartBar size={24} weight="duotone" className="text-white" />}
        gradient="from-[#2D5A4C] to-[#1E4136]"
        accents={['#00AEEF', '#80C51F']}
        actions={(
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setTempLayout([...layout]); setCustomizeOpen(true); }}
            data-testid="customize-dashboard-btn"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <Sliders size={16} className="mr-1.5" />
            Personnaliser
          </Button>
        )}
      />

      {/* KPI Cards Row 1 */}
      {(w.kpi_prospects || w.kpi_membres || w.kpi_dossiers || w.kpi_contributions) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 animate-stagger [&>div]:min-w-0 [&_p]:break-words">
          {w.kpi_prospects && (
            <Card className="border-[#E8E6E1] shadow-sm hover:-translate-y-0.5 transition-transform duration-200 motion-fade-up motion-fade-up-delay-0 motion-shine" data-testid="kpi-prospects">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Prospects actifs</p>
                    <p className="text-3xl font-bold text-[#1A2421] mt-2" style={{ fontFamily: 'Manrope' }}>
                      {stats?.prospects_actifs || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-[#2D5A4C]/10 rounded-lg">
                    <Users size={24} className="text-[#2D5A4C]" weight="duotone" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {w.kpi_membres && (
            <Card className="border-[#E8E6E1] shadow-sm hover:-translate-y-0.5 transition-transform duration-200 motion-fade-up motion-fade-up-delay-1 motion-shine" data-testid="kpi-membres">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Membres actifs</p>
                    <p className="text-3xl font-bold text-[#1A2421] mt-2" style={{ fontFamily: 'Manrope' }}>
                      {stats?.membres_actifs || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-[#8DB0A1]/20 rounded-lg">
                    <Buildings size={24} className="text-[#2D5A4C]" weight="duotone" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {w.kpi_dossiers && (
            <Card className="border-[#E8E6E1] shadow-sm hover:-translate-y-0.5 transition-transform duration-200 motion-fade-up motion-fade-up-delay-2 motion-shine" data-testid="kpi-dossiers">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Dossiers ouverts</p>
                    <p className="text-3xl font-bold text-[#1A2421] mt-2" style={{ fontFamily: 'Manrope' }}>
                      {stats?.dossiers_ouverts || 0}
                    </p>
                    {stats?.dossiers_bloques > 0 && (
                      <p className="text-xs text-[#D47A62] mt-1 flex items-center gap-1">
                        <Warning size={12} /> {stats.dossiers_bloques} bloques
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-[#D47A62]/10 rounded-lg">
                    <Folder size={24} className="text-[#D47A62]" weight="duotone" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {w.kpi_contributions && (
            <Card className="border-[#E8E6E1] shadow-sm hover:-translate-y-0.5 transition-transform duration-200 motion-fade-up motion-fade-up-delay-3 motion-shine" data-testid="kpi-contributions">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Contributions (mois)</p>
                    <p className="text-3xl font-bold text-[#1A2421] mt-2" style={{ fontFamily: 'Manrope' }}>
                      {(stats?.contributions_mois || 0).toLocaleString('fr-FR')} EUR
                    </p>
                  </div>
                  <div className="p-3 bg-[#E8A365]/20 rounded-lg">
                    <CurrencyDollar size={24} className="text-[#E8A365]" weight="duotone" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* KPI Cards Row 2 */}
      {(w.kpi_conversion || w.kpi_paiements || w.kpi_relances) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {w.kpi_conversion && (
            <Card className="border-[#E8E6E1] shadow-sm" data-testid="kpi-conversion">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Taux de conversion</p>
                    <p className="text-2xl font-bold text-[#2D5A4C] mt-1" style={{ fontFamily: 'Manrope' }}>
                      {stats?.taux_conversion || 0}%
                    </p>
                  </div>
                  <TrendUp size={32} className="text-[#2D5A4C]" weight="duotone" />
                </div>
              </CardContent>
            </Card>
          )}

          {w.kpi_paiements && (
            <Card className="border-[#E8E6E1] shadow-sm" data-testid="kpi-paiements">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Paiements en attente</p>
                    <p className="text-2xl font-bold text-[#E8A365] mt-1" style={{ fontFamily: 'Manrope' }}>
                      {stats?.paiements_en_attente || 0}
                    </p>
                  </div>
                  <Clock size={32} className="text-[#E8A365]" weight="duotone" />
                </div>
              </CardContent>
            </Card>
          )}

          {w.kpi_relances && (
            <Card className="border-[#E8E6E1] shadow-sm" data-testid="kpi-relances">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5E6D66]">Relances du jour</p>
                    <p className="text-2xl font-bold text-[#D47A62] mt-1" style={{ fontFamily: 'Manrope' }}>
                      {stats?.relances_jour || 0}
                    </p>
                  </div>
                  <Target size={32} className="text-[#D47A62]" weight="duotone" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Charts Row */}
      {(w.chart_pipeline || w.chart_dossiers) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {w.chart_pipeline && (
            <Card className="border-[#E8E6E1] shadow-sm" data-testid="pipeline-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                  Pipeline commercial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={pipelineData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                      <XAxis type="number" tick={{ fill: '#5E6D66', fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fill: '#5E6D66', fontSize: 11 }} width={100} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: '8px' }} />
                      <Bar dataKey="value" fill="#2D5A4C" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {w.chart_dossiers && (
            <Card className="border-[#E8E6E1] shadow-sm" data-testid="dossiers-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                  Statuts des dossiers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px] flex items-center justify-center">
                  {dossierData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={dossierData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                          {dossierData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E8E6E1', borderRadius: '8px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-[#5E6D66] text-sm">Aucun dossier</p>
                  )}
                </div>
                {dossierData.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-4 justify-center">
                    {dossierData.map((item, index) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span className="text-xs text-[#5E6D66] capitalize">{item.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Territories & Activity Row */}
      {(w.stat_territoires || w.activite_recente) && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 [&>div]:min-w-0">
          {w.stat_territoires && (
            <Card className="border-[#E8E6E1] shadow-sm xl:col-span-1" data-testid="territory-stats">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                  Repartition territoriale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {territories.map((t) => {
                    const count = stats?.territory_stats?.[t.name] || 0;
                    return (
                      <div key={t.abbr} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="w-10 justify-center text-[#2D5A4C] border-[#2D5A4C]">
                            {t.abbr}
                          </Badge>
                          <span className="text-sm text-[#5E6D66]">{t.name}</span>
                        </div>
                        <span className="text-sm font-semibold text-[#1A2421]">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {w.activite_recente && (
            <Card className={`border-[#E8E6E1] shadow-sm ${w.stat_territoires ? 'xl:col-span-2' : 'xl:col-span-3'}`} data-testid="recent-activity">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                  Activite recente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activities.length === 0 ? (
                    <p className="text-[#5E6D66] text-sm text-center py-8">Aucune activite recente</p>
                  ) : (
                    activities.map((activity, index) => (
                      <div key={index} className="flex items-start gap-3 pb-3 border-b border-[#E8E6E1] last:border-0">
                        <div className={`p-2 rounded-lg ${
                          activity.type === 'contact' ? 'bg-[#2D5A4C]/10' :
                          activity.type === 'opportunity' ? 'bg-[#E8A365]/20' :
                          'bg-[#D47A62]/10'
                        }`}>
                          {activity.type === 'contact' ? <Users size={16} className="text-[#2D5A4C]" /> :
                           activity.type === 'opportunity' ? <Target size={16} className="text-[#E8A365]" /> :
                           <Folder size={16} className="text-[#D47A62]" />}
                        </div>
                        <div className="flex-1 min-w-0 break-words">
                          <p className="text-sm font-medium text-[#1A2421]">{activity.title}</p>
                          <p className="text-xs text-[#5E6D66] capitalize">{activity.type} &bull; {activity.action}</p>
                        </div>
                        <span className="text-xs text-[#5E6D66]">
                          {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString('fr-FR') : ''}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Widget : Conventions en attente de signature */}
          <Card className="border-[#E8E6E1] shadow-sm xl:col-span-2" data-testid="widget-conventions-pending">
            <CardHeader className="flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                Conventions en attente de signature
              </CardTitle>
              <Badge variant="outline" className="text-xs bg-orange-50 border-orange-200 text-orange-700">
                {conventionsPending.total}
              </Badge>
            </CardHeader>
            <CardContent>
              {conventionsPending.items.length === 0 ? (
                <p className="text-[#5E6D66] text-sm text-center py-6">Aucune convention en attente</p>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {conventionsPending.items.slice(0, 8).map((c) => (
                    <div key={c.id} className="flex items-center justify-between p-2 bg-[#F9F8F5] rounded hover:bg-[#F0EFEB] transition-colors" data-testid={`pending-conv-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2421] truncate">{c.title || c.contract_reference}</p>
                        <p className="text-xs text-[#5E6D66] truncate">
                          {c.cooperator_name} · <span className="font-mono">{c.contract_reference}</span>
                        </p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] ml-2 flex-shrink-0 ${c.status === 'validated' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                        {c.status === 'validated' ? 'Validée' : 'Brouillon'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Widget : Missions en cours par territoire */}
          <Card className="border-[#E8E6E1] shadow-sm" data-testid="widget-missions-by-territory">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <MapPin size={16} className="text-[#2D5A4C]" />
                Missions en cours
              </CardTitle>
              <p className="text-xs text-[#5E6D66]">Par territoire · {missionsByTerritory.total_missions} au total</p>
            </CardHeader>
            <CardContent>
              {missionsByTerritory.items.length === 0 ? (
                <p className="text-[#5E6D66] text-sm text-center py-6">Aucune mission en cours</p>
              ) : (
                <div className="space-y-2">
                  {missionsByTerritory.items.map((t) => (
                    <div key={t.territoire} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`mission-terr-${t.territoire}`}>
                      <span className="font-medium text-[#1A2421]">{t.territoire}</span>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {t.cipaesse > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">CIPAESSE {t.cipaesse}</span>}
                        {t.cipascop > 0 && <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">CIPASCOP {t.cipascop}</span>}
                        <span className="font-semibold text-[#1A2421] min-w-[24px] text-right">{t.count}</span>
                      </div>
                    </div>
                  ))}
                  {missionsByTerritory.total_amount > 0 && (
                    <div className="pt-2 mt-2 border-t border-[#E8E6E1] flex items-center justify-between text-xs">
                      <span className="text-[#5E6D66]">Prévisionnel total</span>
                      <span className="font-bold text-[#2D5A4C]">{missionsByTerritory.total_amount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section analytique — Devis */}
      <div className="space-y-4" data-testid="quotes-analytics-section">
        <QuotesGlobalKpi />
        <QuotesByTerritoryPanel />
      </div>

      {/* Customize Dialog with Drag & Drop */}
      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="max-w-md" data-testid="customize-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Sliders size={20} className="text-[#2D5A4C]" />
              Personnaliser le tableau de bord
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Glissez-deposez pour reordonner. Activez ou desactivez les widgets.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-1 pr-1">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={tempLayout.map(wt => wt.id)} strategy={verticalListSortingStrategy}>
                {tempLayout.map((item) => (
                  <SortableWidgetItem key={item.id} item={item} onToggle={handleToggleWidget} />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" size="sm" onClick={handleResetLayout} className="text-[#5E6D66]" data-testid="reset-layout-btn">
              <ArrowsClockwise size={14} className="mr-1" />
              Reinitialiser
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCustomizeOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveLayout} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="save-layout-btn">
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
